/**
 * MonitorDemoPage — `/monitor/demo`. PUBLIC wow moment for visitors who haven't
 * signed up. No token, no API: a self-contained, instant, reliable showcase of
 * what Monitor feels like — the "47 trackers found" reveal, the animated savings
 * counter, and one-tap blocking — bilingual (English / Hebrew, RTL) and
 * mobile-first. CTA sends them to the free app.
 *
 * Design language matches the consumer app (`/app/*`): friendly, clean, big
 * numbers, no jargon. Inspired by Revolut's counters + Duolingo's reward beats.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mic,
  MapPin,
  Globe,
  Eye,
  ShieldCheck,
  Wallet,
  Sparkles,
  ArrowRight,
} from 'lucide-react';

import { c, font } from '../app/theme';
import { CountUp } from '../app/CountUp';
import { getLang, saveLang, dir, t, type Lang } from '../app/i18n';
import { useInstallPrompt } from '../app/useInstallPrompt';

const FOUND = 47;
const SAVED_USD = 127;

interface DemoTracker {
  name: string;
  what_en: string;
  what_he: string;
  count: number;
  icon: typeof Mic;
}

const TRACKERS: DemoTracker[] = [
  { name: 'Instagram', what_en: 'your microphone', what_he: 'המיקרופון שלך', count: 23, icon: Mic },
  { name: 'Google', what_en: 'your location', what_he: 'המיקום שלך', count: 184, icon: MapPin },
  { name: 'TikTok', what_en: 'your browsing', what_he: 'הגלישה שלך', count: 57, icon: Globe },
  { name: 'Meta Pixel', what_en: 'sites you visit', what_he: 'אתרים שאתה מבקר בהם', count: 412, icon: Eye },
];

export function MonitorDemoPage() {
  const [lang, setLang] = useState<Lang>(getLang());
  const [phase, setPhase] = useState<'scanning' | 'revealed'>('scanning');
  const [blocked, setBlocked] = useState<Set<string>>(new Set());
  const { canInstall, installed, promptInstall } = useInstallPrompt();
  const [allBlocked, setAllBlocked] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setPhase('revealed'), 1300);
    return () => window.clearTimeout(id);
  }, []);

  function pick(l: Lang) {
    setLang(l);
    saveLang(l);
  }

  const blockedCount = allBlocked ? FOUND : blocked.size;
  const watching = Math.max(0, FOUND - blockedCount);
  const tx = (k: string) => t(k, lang);

  return (
    <div dir={dir(lang)} style={{ fontFamily: font, background: c.canvas, minHeight: '100dvh', color: c.ink }}>
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '0 0 40px' }}>
        {/* top bar */}
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 18px',
          }}
        >
          <span style={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em' }}>Monitor</span>
          <div style={{ display: 'flex', gap: 4, background: c.surface, border: `1px solid ${c.rim}`, borderRadius: 999, padding: 3 }}>
            <LangBtn active={lang === 'en'} onClick={() => pick('en')}>EN</LangBtn>
            <LangBtn active={lang === 'he'} onClick={() => pick('he')}>עב</LangBtn>
          </div>
        </header>

        {/* hero */}
        <div style={{ padding: '6px 22px 0' }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: '6px 0 6px', lineHeight: 1.15 }}>
            {tx('hero_title')}
          </h1>
          <p style={{ fontSize: 15, color: c.inkMuted, margin: '0 0 18px', lineHeight: 1.5 }}>
            {tx('hero_sub')}
          </p>
        </div>

        {/* scan reveal */}
        <div style={{ padding: '0 18px' }}>
          {phase === 'scanning' ? (
            <ScanningCard label={tx('scanning')} />
          ) : (
            <div
              style={{
                background: `linear-gradient(135deg, ${c.accent}, #3b82f6)`,
                borderRadius: 22,
                padding: '26px 20px',
                color: '#fff',
                textAlign: 'center',
                boxShadow: '0 16px 40px -18px rgba(29,78,216,0.6)',
              }}
            >
              <div style={{ fontSize: 64, fontWeight: 800, lineHeight: 1 }}>
                <CountUp target={FOUND} durationMs={1100} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, opacity: 0.95, marginTop: 8 }}>
                {tx('trackers_found')}
              </div>
            </div>
          )}
        </div>

        {phase === 'revealed' && (
          <>
            {/* stat row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, padding: '14px 18px 0' }}>
              <Stat n={FOUND} label={tx('found')} fg={c.ink} bg={c.surface} rim={c.rim} />
              <Stat n={blockedCount} label={tx('blocked')} fg={c.good} bg={c.goodSoft} rim={c.goodRim} />
              <Stat n={watching} label={tx('still_watching')} fg={c.danger} bg={c.dangerSoft} rim={c.dangerRim} />
            </div>

            {/* savings hero */}
            <div style={{ padding: '16px 18px 0' }}>
              <div
                style={{
                  background: c.goodSoft,
                  border: `1px solid ${c.goodRim}`,
                  borderRadius: 22,
                  padding: '22px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                }}
              >
                <span style={{ flexShrink: 0, width: 52, height: 52, borderRadius: 16, background: '#fff', color: c.good, display: 'grid', placeItems: 'center' }}>
                  <Wallet size={26} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 40, fontWeight: 800, color: c.good, lineHeight: 1 }}>
                    <CountUp target={SAVED_USD} prefix="$" durationMs={1400} delayMs={300} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: c.ink, marginTop: 4 }}>{tx('saved_this_month')}</div>
                  <div style={{ fontSize: 12.5, color: c.inkMuted, marginTop: 2 }}>{tx('savings_sub')}</div>
                </div>
              </div>
            </div>

            {/* tracker list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '16px 18px 0' }}>
              {TRACKERS.map((tr) => {
                const isBlocked = allBlocked || blocked.has(tr.name);
                return (
                  <TrackerCard
                    key={tr.name}
                    tr={tr}
                    lang={lang}
                    blocked={isBlocked}
                    onBlock={() => setBlocked((s) => new Set(s).add(tr.name))}
                  />
                );
              })}
            </div>

            {/* block all + reward */}
            <div style={{ padding: '16px 18px 0' }}>
              {watching > 0 ? (
                <button
                  type="button"
                  onClick={() => setAllBlocked(true)}
                  style={primaryBtn}
                >
                  {tx('block_all')} ({watching})
                </button>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: c.goodSoft,
                    border: `1px solid ${c.goodRim}`,
                    borderRadius: 16,
                    padding: '14px 16px',
                    color: c.good,
                    fontWeight: 700,
                    fontSize: 15,
                  }}
                >
                  <ShieldCheck size={20} /> {tx('nice')}
                </div>
              )}
            </div>

            {/* proof line */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '16px 22px 0', color: c.inkSubtle, fontSize: 12.5, lineHeight: 1.5 }}>
              <Sparkles size={15} style={{ color: c.accent, flexShrink: 0, marginTop: 2 }} />
              <span>{tx('proof')}</span>
            </div>

            {/* CTA */}
            <div style={{ padding: '22px 18px 0' }}>
              <Link to="/app/start" style={{ textDecoration: 'none' }}>
                <div style={{ ...primaryBtn, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  {tx('start_free')} <ArrowRight size={20} />
                </div>
              </Link>
              <p style={{ fontSize: 12.5, color: c.inkSubtle, textAlign: 'center', margin: '12px 0 0', lineHeight: 1.5 }}>
                {tx('free_line')}
              </p>
              {canInstall && !installed && (
                <button
                  onClick={() => void promptInstall()}
                  style={{
                    ...primaryBtn,
                    background: '#0f172a',
                    marginTop: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  {lang === 'he' ? '📲 התקן כאפליקציה' : '📲 Install as app'}
                </button>
              )}
              {installed && (
                <p style={{ fontSize: 12, color: '#15803d', textAlign: 'center', margin: '10px 0 0', fontWeight: 700 }}>
                  {lang === 'he' ? '✓ מותקן' : '✓ Installed'}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const primaryBtn = {
  width: '100%',
  minHeight: 54,
  fontSize: 17,
  fontWeight: 800,
  color: '#fff',
  background: c.accent,
  border: 'none',
  borderRadius: 16,
  cursor: 'pointer',
} as const;

function LangBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minWidth: 38,
        padding: '6px 10px',
        fontSize: 13,
        fontWeight: 700,
        border: 'none',
        borderRadius: 999,
        cursor: 'pointer',
        color: active ? '#fff' : c.inkMuted,
        background: active ? c.accent : 'transparent',
      }}
    >
      {children}
    </button>
  );
}

function ScanningCard({ label }: { label: string }) {
  return (
    <div
      style={{
        background: c.surface,
        border: `1px solid ${c.rim}`,
        borderRadius: 22,
        padding: '34px 20px',
        textAlign: 'center',
      }}
    >
      <div style={{ position: 'relative', width: 72, height: 72, margin: '0 auto 16px' }}>
        <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: c.accentSoft, animation: 'gi-ping 1.3s cubic-bezier(0,0,0.2,1) infinite' }} />
        <span style={{ position: 'absolute', inset: 14, borderRadius: '50%', background: c.accent, display: 'grid', placeItems: 'center', color: '#fff' }}>
          <Eye size={26} />
        </span>
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: c.ink }}>{label}</div>
      {/* keyframes (scoped, harmless if duplicated) */}
      <style>{'@keyframes gi-ping{75%,100%{transform:scale(1.7);opacity:0}}'}</style>
    </div>
  );
}

function Stat({ n, label, fg, bg, rim }: { n: number; label: string; fg: string; bg: string; rim: string }) {
  return (
    <div style={{ background: bg, border: `1px solid ${rim}`, borderRadius: 16, padding: '16px 8px', textAlign: 'center' }}>
      <div style={{ fontSize: 30, fontWeight: 800, color: fg, lineHeight: 1 }}>
        <CountUp target={n} durationMs={900} />
      </div>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: c.inkMuted, marginTop: 6 }}>{label}</div>
    </div>
  );
}

function TrackerCard({
  tr,
  lang,
  blocked,
  onBlock,
}: {
  tr: DemoTracker;
  lang: Lang;
  blocked: boolean;
  onBlock: () => void;
}) {
  const Icon = tr.icon;
  const what = lang === 'he' ? tr.what_he : tr.what_en;
  return (
    <div
      style={{
        background: c.surface,
        border: `1px solid ${c.rim}`,
        borderRadius: 16,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        transition: 'transform 120ms ease',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: 12,
          background: blocked ? c.goodSoft : c.dangerSoft,
          color: blocked ? c.good : c.danger,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Icon size={22} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{tr.name}</div>
        <div style={{ fontSize: 13, color: c.inkMuted, marginTop: 2 }}>
          {t('touched', lang)} {what} · {tr.count}×
        </div>
      </div>
      {blocked ? (
        <span style={{ flexShrink: 0, fontSize: 13, fontWeight: 700, color: c.good, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ShieldCheck size={16} /> {t('blocked_badge', lang)}
        </span>
      ) : (
        <button
          type="button"
          onClick={onBlock}
          style={{
            flexShrink: 0,
            minHeight: 44,
            padding: '0 18px',
            fontSize: 14,
            fontWeight: 700,
            color: '#fff',
            background: c.accent,
            border: 'none',
            borderRadius: 12,
            cursor: 'pointer',
          }}
        >
          {t('block', lang)}
        </button>
      )}
    </div>
  );
}

export default MonitorDemoPage;
